"use client";

import Image from "next/image";
import { useTheme } from "next-themes";

import FlickeringGrid from "@/components/magicui/flickeringGrid";
import { Icons } from "@/components/shared/icons";
import { buttonVariants } from "@/components/ui/button";
import { ContainerScroll } from "@/components/ui/container-scroll-animation";
import { Link } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import * as m from "@/paraglide/messages";
import { useAuth } from "@/services/context/auth/AuthContext";

export default function HeroLanding({ start }: { start?: number }) {
  const { user } = useAuth();
  const { theme } = useTheme();

  return (
    <section
      className="bg-background relative  space-y-6 pt-2 md:pt-12 pb-20 sm:py-16 lg:py-16"
      id="hero"
    >
      <FlickeringGrid
        className={cn(
          "[mask-image:radial-gradient(circle_at_center,white,transparent)] z-0 absolute inset-0 size-full",
        )}
        squareSize={2}
        gridGap={8}
        color={theme === "light" ? "#6B7280" : "#fff"}
        maxOpacity={0.2}
        flickerChance={0.8}
        height={1200}
      />
      <div className="z-10 container flex max-w-5xl flex-col items-center gap-5 text-center">
        {/* Want animations? Check here: https://github.com/mickasmt/next-saas-stripe-starter/blob/76eb9f2b70b29c7a734ff0e5b047796ed2dac28d/app/(marketing)/page.tsx */}
        <div
          className={cn(
            buttonVariants({
              variant: "outline",
              size: "sm",
            }),
            "gap-2 z-10 rounded-full",
          )}
        >
          <p className="inline-flex  px-4  transition ease-out hover:text-neutral-600 hover:duration-300">
            <span className="mr-3">🎉</span> {m.nice_orange_lizard_dream()}
          </p>
        </div>

        <h1
          className="max-w-4xl text-transparent dark:from-white dark:to-white/40 animate-fade-up  opacity-0 text-2xl font-extrabold tracking-tight leading-normal sm:text-5xl md:text-6xl lg:text-7xl pb-4 text-balance bg-gradient-to-br from-black from-30% to-black/60 bg-clip-text"
          style={{ animationDelay: "0.25s", animationFillMode: "forwards" }}
        >
          {m.catchphrase()}
        </h1>

        <p
          className="max-w-2xl animate-fade-up  opacity-0 text-balance leading-normal text-primary sm:text-xl sm:leading-8"
          style={{ animationDelay: "0.6s", animationFillMode: "forwards" }}
        >
          {m.equal_civil_impala_belong()} -
          <strong> {m.candid_steep_kangaroo_zip()}</strong>.
        </p>

        <div
          className="flex flex-col animate-fade-up  opacity-0 justify-center"
          style={{ animationDelay: "1s", animationFillMode: "forwards" }}
        >
          {!user?.email ? (
            <Link
              className={cn(
                buttonVariants({ variant: "flicker", size: "sm" }),
                " gap-2 px-4 md:flex shimmer",
              )}
              href="/signup"
            >
              <span>{m.start_free_btn()}</span>
              <Icons.arrowRight className="size-4" />
            </Link>
          ) : (
            <Link
              className={cn(
                buttonVariants({ variant: "default", size: "sm" }),
                " gap-2 px-4 md:flex",
              )}
              href="/dashboard"
            >
              <span>{m.link_to_dashboard()}</span>
              <Icons.arrowRight className="size-4" />
            </Link>
          )}
          <Link
            href="#portfolio"
            className={cn(
              buttonVariants({
                variant: "link",
                size: "sm",
              }),

              "  md:flex text-muted-foreground underline",
            )}
          >
            <span>{m.find_out_more()}</span>
          </Link>
        </div>
      </div>

      <ContainerScroll start={start}>
        {theme === "dark" ? (
          <Image
            src={"/images/marketing/dash_dark.webp"}
            alt="ARYA Crypto Dashboard Dark preview"
            width="3394"
            height="2026"
            decoding="async"
            data-nimg="1"
            priority
            loading="eager"
            className="mx-auto rounded-2xl object-cover h-full object-left-top"
            draggable={false}
          />
        ) : (
          <Image
            src={"/images/marketing/dash_light.webp"}
            alt="ARYA Crypto Dashboard preview"
            width="3394"
            height="2026"
            decoding="async"
            data-nimg="1"
            priority
            loading="eager"
            className="mx-auto rounded-2xl object-cover h-full object-left-top"
            draggable={false}
          />
        )}
      </ContainerScroll>
    </section>
  );
}
